import { format } from 'date-fns'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  type Claim,
  createDraftClaim,
  patchClaimRequest,
  requestClaimPhotoExtraction,
  uploadClaimPhotoRequest,
} from '../../../../models/claim'
import { claimToStep1Defaults, newClaimDefaults, type Step1FormValues } from './claimFormValues'
import { ClaimPhotoSection } from './ClaimPhotoSection'
import { ClaimReporterSection } from './ClaimReporterSection'
import { ClaimStepShell } from './ClaimStepShell'

const photoKey = (file: File) => `${file.name}:${file.size}`

interface ClaimPhotosFormProps {
  /** Existing claim id (edit mode); omit for a brand-new claim (created on Next). */
  claimId?: string
  /** On-behalf org scope (CAI user acting for a customer) — used on every request. */
  orgId?: string
  /** On-behalf target user (create only) — sets created_by to the business owner. */
  userId?: string
  claim?: Claim
  reporterName?: string
  reporterPhone?: string
  /** Called with the new claim id once the draft is created. */
  onCreated: (id: string) => void
  onSaved: (claim: Claim) => void
}

export function ClaimPhotosForm({
  claimId,
  orgId,
  userId,
  claim,
  reporterName,
  reporterPhone,
  onCreated,
  onSaved,
}: ClaimPhotosFormProps) {
  const [saving, setSaving] = useState(false)
  const { control, handleSubmit, setValue, getValues } = useForm<Step1FormValues>({
    defaultValues: claim ? claimToStep1Defaults(claim) : newClaimDefaults(reporterName, reporterPhone),
  })
  // Files already uploaded this session, so re-submitting doesn't re-send them.
  const uploadedKeys = useRef<Set<string>>(new Set())

  async function onValid(values: Step1FormValues) {
    setSaving(true)
    try {
      let id = claimId
      if (!id) {
        const created = await createDraftClaim(orgId ? { orgId, userId } : undefined)
        id = created.id
        onCreated(id)
        // Reflect the new claim in the URL (…/claims/new → …/claims/<id>) without a
        // router navigation, so the wizard keeps its state instead of remounting into
        // EditClaim. Works for both the internal and customer paths; org_id is kept so
        // a refresh can still scope the fetch.
        const claimPath = window.location.pathname.replace(/\/new$/, `/${id}`)
        window.history.replaceState(
          window.history.state,
          '',
          orgId ? `${claimPath}?org_id=${orgId}` : claimPath
        )
      }

      // Save on the CTA, not as each file is added; skip anything already uploaded.
      if (!values.photoDeferred) {
        const pending = values.photos.filter((f) => !uploadedKeys.current.has(photoKey(f)))
        await Promise.all(pending.map((f) => uploadClaimPhotoRequest(id!, f, orgId)))
        pending.forEach((f) => uploadedKeys.current.add(photoKey(f)))
      }

      const reporter = { name: values.name, phone: values.phone, relationships: values.relationships }
      let saved = await patchClaimRequest(
        id,
        values.photoDeferred
          ? { reporter, photoUploadDeferred: true, photoDeferralReason: values.deferralReason }
          : { reporter },
        orgId
      )

      if (!values.photoDeferred && saved.photos.length > 0) {
        const capturedAt = values.capturedAt ? format(values.capturedAt, "yyyy-MM-dd'T'HH:mm:ss") : undefined
        // Reading the uploads only pre-fills later steps, so a failure must not block the claim.
        saved = await requestClaimPhotoExtraction(id, orgId, capturedAt).catch(() => saved)
      }
      onSaved(saved)
    } catch {
      toast.error('Could not save the claim. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ClaimStepShell
      step={1}
      title="Upload Photos"
      subtitle="Add photos or documents from the scene — anything you have. We'll auto-fill what we can from your images."
    >
      {/* Intentionally NOT a native <form>: Radix Checkbox renders a hidden
          form-participating input that loops on click inside a <form>. RHF owns all
          state, so we submit via the button's onClick instead. */}
      <ClaimReporterSection control={control} />

      <ClaimPhotoSection
        control={control}
        setValue={setValue}
        getValues={getValues}
        existingPhotos={claim?.photos ?? []}
      />

      <div className="flex justify-end">
        <Button type="button" onClick={handleSubmit(onValid)} loading={saving ? 'Saving…' : undefined}>
          Next →
        </Button>
      </div>
    </ClaimStepShell>
  )
}
